import React, { useMemo, useState, useEffect } from 'react'
import { useTable, useExpanded } from 'react-table'
import {
  FiChevronDown,
  FiChevronUp,
  FiPaperclip,
  FiBell,
} from 'react-icons/fi'
import useQuery from '_hooks/useQuery'
import { useModal } from '_hooks/useModal'
import { getUserInfo, getRemindPaymentMessage } from '_utils/localData'
import {
  BUYER_CANCELED,
  BUYER_COLLECTED,
  BUYER_PAID,
  BUYER_REFUNDED,
  BUYER_UNPAID,
  HOST_ORDER_CANCELED,
  HOST_ORDER_COLLECTED,
  HOST_ORDER_UNPAID,
  HOST_ORDER_VERIFIED,
  HOST_ORDER_SUBMITTED_UNPAID,
  HOST_ORDER_SUBMITTED_VERIFIED,
  HOST_ORDER_SUBMITTED_COLLECTED,
  HOST_ORDER_REFUNDED,
  PAYMENT_METHOD_HIT_PAY,
} from '_utils/constant'
import HitPayRibbon from '_components/HitPayRibon'
import RemindPaymentModel from '../../../components/RemindPaymentModel'
import ModalMessage from '../../../components/ModalMessage'
import ModalWarning from '../../../components/ModalWarning'
import ModalDetailOrder from '../../../components/ModalDetailOrder'
import ModalPaymentScreen from '../../../components/ModalPaymentScreen'
import ModalRefundOrder from '../../../components/ModalRefundOrder'
import { useOrderTableSort, useOrderStatusActions } from '../../hooks'
import './style.scss'

export default function TableOrderList({
  listOrder,
  setTimeRender,
  id,
  products = [],
  sorting = { field: '', order: '', type: '' },
  setSorting,
}) {
  const query = useQuery()
  const eventId = query.get('eventId')
  const targetEventId = id || eventId
  const isByAdmin = query.get('isByAdmin') === 'true'
  const data = useMemo(() => {
    if (listOrder && listOrder.length > 0) {
      return listOrder
    }
    return []
  }, [listOrder])

  const paymentModal = useModal(false)
  const [selectedPaymentOrder, setSelectedPaymentOrder] = useState(null)
  const remindModal = useModal(false)
  const [selectedRemindOrder, setSelectedRemindOrder] = useState(null)
  const [remindMessage, setRemindMessage] = useState({ isDefault: true, message: '' })
  const messageModal = useModal(false)
  const [selectedMessageOrder, setSelectedMessageOrder] = useState(null)
  const detailModal = useModal(false)
  const [selectedDetailOrder, setSelectedDetailOrder] = useState(null)
  const warningModal = useModal(false)
  const [selectedWarningOrder, setSelectedWarningOrder] = useState(null)
  const refundModal = useModal(false)
  const [selectedRefundOrder, setSelectedRefundOrder] = useState(null)

  useEffect(() => {
    const msg = getRemindPaymentMessage()
    if (msg) setRemindMessage(msg)
  }, [])

  const handleOpenWarning = (e, order) => {
    if (e && e.preventDefault) e.preventDefault()
    setSelectedWarningOrder(order)
    warningModal.openModal()
  }

  const openRefundModal = (order) => {
    setSelectedRefundOrder(order)
    refundModal.openModal()
  }

  const openPaymentScreen = (e, order) => {
    e.stopPropagation()
    setSelectedPaymentOrder(order)
    paymentModal.openModal()
  }

  const {
    updatingRef,
    handleConfirmRefund,
    handleInvalidPayment,
    handleCancelOrder,
    onChangeSelect,
  } = useOrderStatusActions({
    targetEventId,
    isByAdmin,
    listOrder,
    setTimeRender,
    openRefundModal,
    detailModal,
    warningModal,
    paymentModal,
  })

  const { handleHeaderSort } = useOrderTableSort(sorting, setSorting)

  const columns = useMemo(
    () => [
      {
        Header: 'NO',
        accessor: (row, i) => row.eOrderNo || i + 1,
        id: 'no',
        sortField: 'eOrderNo',
        sortType: 'number',
        sortable: true,
        Cell: ({ row, value }) => {
          const order = row.original
          const checkPayHitPay = order?.paymentType === PAYMENT_METHOD_HIT_PAY
          return (
            <>
              {checkPayHitPay && (
                <HitPayRibbon isMobile={false} />
              )}
              <span className={`muted od-no-text ${checkPayHitPay ? 'has-hitpay' : ''}`}>
                {value}
              </span>
            </>
          )
        },
      },
      {
        Header: 'NAME',
        accessor: 'uName',
        id: 'name',
        sortField: 'uName',
        sortType: 'string',
        sortable: true,
        Cell: ({ row, value }) => {
          const order = row.original
          const checkPayHitPay = order?.paymentType === PAYMENT_METHOD_HIT_PAY
          return (
            <div
              className={`od-row-name-cell ${checkPayHitPay ? 'has-hitpay' : ''}`}
              onClick={(e) => {
                e.stopPropagation()
                setSelectedDetailOrder(order)
                detailModal.openModal()
              }}
            >
              {checkPayHitPay && (
                <HitPayRibbon isMobile={true} />
              )}
              <span className="od-name-text" title={value}>
                {value}
              </span>
              {order?.comment && (
                <a
                  href="#"
                  className="ml-1"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setSelectedMessageOrder(order)
                    messageModal.openModal()
                  }}
                >
                  <i className="icon-new" />
                </a>
              )}
            </div>
          )
        },
      },
      {
        Header: 'QTY',
        accessor: (row) =>
          row.totalQuantity ||
          (row.allProductPerOrder || []).reduce(
            (sum, item) => sum + (item.pQuantity || 0),
            0
          ),
        id: 'qty',
        sortField: 'totalQuantity',
        sortType: 'number',
        sortable: true,
        Cell: ({ value }) => <span className="muted">{value}</span>,
      },
      {
        Header: 'PRICE',
        accessor: (row) => {
          if (row.totalFee !== undefined) {
            return row.totalFee
          }
          return row.totalPricePerOrder || 0
        },
        id: 'price',
        sortField: 'totalFee',
        sortType: 'number',
        sortable: true,
        Cell: ({ row }) => {
          const order = row.original
          const isRefunded = order?.buyerStatus === BUYER_REFUNDED
          const discountPerOrder = order?.discountPerOrder
          const originalPrice = (order?.totalPricePerOrder || 0) + (order?.adminCostPerOrder || 0)
          const totalFee = order?.totalFee || 0
          return (
            <div className='od-price-cell'>
              {discountPerOrder ? (
                <span className="price-original">
                  ${Number(originalPrice).toFixed(2)}
                </span>
              ) : null}
              <span className="price-final">${Number(totalFee).toFixed(2)}</span>
              {isRefunded ? <span className="price-refunded"> (Refunded)</span> : null}
            </div>
          )
        },
      },
      {
        Header: 'STATUS',
        id: 'status',
        sortField: 'buyerStatus',
        sortType: 'number',
        sortable: true,
        Cell: ({ row }) => {
          const order = row.original
          const buyerStatus = order?.buyerStatus

          if (buyerStatus === BUYER_UNPAID) {
            return (
                <span
                  className="bell-not-paid"
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedRemindOrder(order)
                    remindModal.openModal()
                  }}
                >
                  <FiBell className='icon-not-paid'/> Not Paid
                </span>
            )
          }

          if (buyerStatus === BUYER_PAID) {
            const isCombined = Boolean(order?.combinedPaymentRef)
            return (
              <span
                className={isCombined ? 'paid-combined' : 'paid'}
                onClick={(e) => openPaymentScreen(e, order)}
              >
                {isCombined ? (
                  <span className="icons-combined" style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <FiPaperclip className='icon-paid' />
                    <FiPaperclip className='icon-paid' style={{ marginLeft: '-4px' }} />
                  </span>
                ) : (
                  <FiPaperclip className='icon-paid' />
                )}
                Paid
              </span>
            )
          }

          if (buyerStatus === BUYER_CANCELED) {
            return <span className="status-badge canceled">Cancelled</span>
          }

          if (buyerStatus === BUYER_COLLECTED) {
            return <span className="status-badge collected">Collected</span>
          }

          if (buyerStatus === BUYER_REFUNDED) {
            return <span className="status-badge refunded">Refunded</span>
          }

          return null
        },
      },
      {
        Header: 'UPDATES',
        id: 'updates',
        sortField: 'status',
        sortType: 'number',
        sortable: true,
        Cell: ({ row }) => {
          const order = row.original
          const currentStatus = order.status
          const isUpdating = updatingRef === order.ref
          const checkPayHitPay = order?.paymentType === PAYMENT_METHOD_HIT_PAY
          const isRefunded = currentStatus === HOST_ORDER_REFUNDED
          const isDisabled = isByAdmin || isUpdating || isRefunded
          return (
            <div className="od-status-cell">
              <select
                className="od-status-select"
                value={currentStatus}
                disabled={isDisabled}
                onChange={(e) =>
                  onChangeSelect(
                    e,
                    order.ref,
                    order.status,
                    order.buyerStatus,
                    order.combinedPaymentRef,
                    order
                  )
                }
                style={{
                  ...(isDisabled
                    ? {
                        opacity: '0.5',
                        pointerEvents: 'none',
                        filter: 'grayscale(1)',
                      }
                    : {}),
                  color:
                    currentStatus === HOST_ORDER_UNPAID ||
                    currentStatus === HOST_ORDER_SUBMITTED_UNPAID
                      ? '#ff6903'
                      : currentStatus === HOST_ORDER_VERIFIED ||
                        currentStatus === HOST_ORDER_SUBMITTED_VERIFIED ||
                        currentStatus === HOST_ORDER_SUBMITTED_COLLECTED ||
                        currentStatus === HOST_ORDER_COLLECTED
                      ? '#51aaaa'
                      : '#7c797a',
                }}
              >
                {!checkPayHitPay && <option value={HOST_ORDER_CANCELED}>Cancelled</option>}
                {!checkPayHitPay && <option value={HOST_ORDER_UNPAID}>Unpaid</option>}
                {!checkPayHitPay && (
                  <option value={HOST_ORDER_SUBMITTED_UNPAID}>
                    Submitted & Unpaid
                  </option>
                )}
                {(checkPayHitPay || isRefunded) && (
                  <option value={HOST_ORDER_REFUNDED}>
                    {isRefunded ? 'Refunded' : 'Refund'}
                  </option>
                )}
                <option value={HOST_ORDER_SUBMITTED_VERIFIED}>
                  Submitted & Verified
                </option>
                <option value={HOST_ORDER_SUBMITTED_COLLECTED}>
                  Submitted & Collected
                </option>
                <option value={HOST_ORDER_VERIFIED}>Verified</option>
                <option value={HOST_ORDER_COLLECTED}>Collected</option>
              </select>
            </div>
          )
        },
      },
    ],
    [isByAdmin, updatingRef]
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
    },
    useExpanded
  )

  return (
    <div>
      <p className="od-hint">
        Click on Names for Order Details and on
        <FiPaperclip />
        for Payment Details.
      </p>

      <div className="table-order-wrapper">
        <table {...getTableProps()} className="table-order-list">
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key: hgKey, ...restHeaderGroupProps } = headerGroup.getHeaderGroupProps()
              return (
                <tr key={hgKey} {...restHeaderGroupProps}>
                  {headerGroup.headers.map((column) => {
                    const { key: colKey, ...restHeaderProps } = column.getHeaderProps()
                    const isSorted = sorting?.field === column.sortField
                    const isAsc = sorting?.order === 'asc'
                    return (
                      <th
                        key={colKey}
                        {...restHeaderProps}
                        className={`col-${column.id} ${column.sortable ? 'sortable' : ''}`}
                        onClick={() => handleHeaderSort(column)}
                        style={column.sortable ? { cursor: 'pointer', userSelect: 'none' } : {}}
                      >
                        <div className="d-flex align-items-center">
                          <span>{column.render('Header')}</span>
                          {column.sortable && (
                            <span
                              className={`sort-icon ${isSorted ? 'active' : ''}`}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                marginLeft: '4px',
                                width: '14px',
                                height: '14px',
                                visibility: isSorted ? 'visible' : 'hidden',
                              }}
                            >
                              {isAsc ? <FiChevronUp /> : <FiChevronDown />}
                            </span>
                          )}
                        </div>
                      </th>
                    )
                  })}
                </tr>
              )
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={headerGroups[0]?.headers?.length || 6}
                  style={{
                    textAlign: 'center',
                    padding: '32px 16px',
                    color: '#888780',
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                >
                  No results found
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                prepareRow(row)
                const { key: rowKey, ...restRowProps } = row.getRowProps()
                return (
                  <React.Fragment key={rowKey}>
                    <tr
                      key={rowKey}
                      {...restRowProps}
                      className={`od-row ${row.isExpanded ? 'od-row-expanded' : ''}`}
                    >
                      {row.cells.map((cell) => {
                        const { key: cellKey, ...restCellProps } = cell.getCellProps()
                        return (
                          <td
                            key={cellKey}
                            {...restCellProps}
                            className={`col-${cell.column.id}`}
                          >
                            {cell.render('Cell')}
                          </td>
                        )
                      })}
                    </tr>
                  </React.Fragment>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Payment Screenshot Modal */}
      <ModalPaymentScreen
        modalPaymentScreen={paymentModal.isOpen}
        togglePaymentScreen={paymentModal.toggleModal}
        data={selectedPaymentOrder || {}}
        handleInvalidPayment={handleInvalidPayment}
      />

      {/* Refund Order Modal */}
      <ModalRefundOrder
        modalRefundOrder={refundModal.isOpen}
        toggleRefundOrder={refundModal.toggleModal}
        data={selectedRefundOrder || {}}
        onConfirmRefund={handleConfirmRefund}
        products={products}
      />

      {/* Remind Payment Modal */}
      {selectedRemindOrder && (
        <RemindPaymentModel
          order={selectedRemindOrder}
          modalRemindPayment={remindModal.isOpen}
          host={getUserInfo()}
          remindMessage={remindMessage}
          toggleDetailOrder={remindModal.toggleModal}
        />
      )}

      {/* Comment Message Modal */}
      {selectedMessageOrder && (
        <ModalMessage
          data={selectedMessageOrder}
          modalMessage={messageModal.isOpen}
          toggleMessage={messageModal.toggleModal}
        />
      )}

      {/* Order Detail Modal */}
      {selectedDetailOrder && (
        <ModalDetailOrder
          id={targetEventId}
          order={selectedDetailOrder}
          modalDetailOrder={detailModal.isOpen}
          toggleDetailOrder={detailModal.toggleModal}
          handleOpenWarning={handleOpenWarning}
          setTimeRender={setTimeRender}
          products={products}
        />
      )}

      {/* Warning Cancel Modal */}
      {selectedWarningOrder && (
        <ModalWarning
          data={selectedWarningOrder}
          modalWarning={warningModal.isOpen}
          toggleWarning={warningModal.toggleModal}
          handleCancel={handleCancelOrder}
        />
      )}
    </div>
  )
}
